<template>
  <div class="flex flex-col gap-1">
    <div
      v-for="(item, index) in stepItems"
      :key="item.value"
      class="flex gap-3"
    >
      <!-- Indicator column -->
      <div class="flex w-8 shrink-0 flex-col items-center">
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors"
          :class="indicatorClass(item.value)"
          :disabled="item.disabled"
          :aria-current="activeStep === item.value ? 'step' : undefined"
          @click="focusStep(item.value)"
        >
          <UIcon
            v-if="isStepComplete(item.value)"
            name="i-heroicons-check-20-solid"
            class="h-4 w-4"
          />
          <UIcon
            v-else
            :name="item.icon || 'i-heroicons-hashtag'"
            class="h-4 w-4"
          />
        </button>
        <div
          v-if="index < stepItems.length - 1"
          class="my-1 w-px flex-1 min-h-6"
          :class="isStepComplete(item.value) ? 'bg-primary' : 'bg-default'"
        />
      </div>

      <!-- Always-visible step body -->
      <div
        class="min-w-0 flex-1 pb-8 transition-opacity"
        :class="[
          activeStep === item.value ? 'opacity-100' : 'opacity-40 hover:opacity-70',
          item.disabled ? 'pointer-events-none opacity-30 hover:opacity-30' : '',
        ]"
      >
        <button
          type="button"
          class="mb-3 text-left"
          :disabled="item.disabled"
          @click="focusStep(item.value)"
        >
          <p
            class="text-sm font-semibold"
            :class="activeStep === item.value ? 'text-primary' : 'text-text-primary'"
          >
            {{ item.title }}
          </p>
          <p v-if="item.description" class="text-xs text-text-secondary">
            {{ item.description }}
          </p>
        </button>

        <!-- Client -->
        <div v-if="item.value === 'client'" class="flex w-full min-w-0 flex-col gap-3">
          <UFormField label="Client" required class="w-full">
            <UIClientpicker class="w-full" />
          </UFormField>
          <div class="flex justify-end">
            <UButton
              label="Continue"
              color="primary"
              trailing-icon="i-heroicons-arrow-right-20-solid"
              :disabled="!effectiveClientId"
              @click="focusStep('customer')"
            />
          </div>
        </div>

        <!-- Customer -->
        <div v-else-if="item.value === 'customer'" class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-2">
            <p class="text-sm text-text-secondary">Who is this report about?</p>
            <div class="flex rounded-lg border border-default p-0.5">
              <button
                type="button"
                class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
                :class="identityMode === 'search'
                  ? 'bg-primary text-white'
                  : 'text-text-secondary hover:text-text-primary'"
                @click="identityMode = 'search'"
              >
                Find
              </button>
              <button
                type="button"
                class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
                :class="identityMode === 'manual'
                  ? 'bg-primary text-white'
                  : 'text-text-secondary hover:text-text-primary'"
                @click="switchToManual"
              >
                Manual
              </button>
            </div>
          </div>

          <div
            v-if="selectedCustomer"
            class="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5"
          >
            <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15">
              <UIcon name="i-heroicons-user-solid" class="h-4 w-4 text-primary" />
            </div>
            <div class="min-w-0 flex-1 space-y-1">
              <div class="flex flex-wrap items-center gap-1.5">
                <p class="text-sm font-medium text-text-primary truncate">{{ selectedCustomer.name }}</p>
                <UBadge v-if="!selectedCustomer.active" variant="subtle" size="sm" color="error">Deactivated</UBadge>
                <UBadge v-if="selectedCustomer.guestUser" variant="subtle" size="sm" color="warning">Guest</UBadge>
              </div>
              <p v-if="selectedCustomer.companyName" class="text-xs text-primary truncate">
                {{ selectedCustomer.companyName }}
              </p>
              <div class="flex flex-col gap-0.5 text-xs text-text-secondary">
                <span>ID {{ selectedCustomer.wuId }}</span>
                <span v-if="selectedCustomer.email" class="truncate">{{ selectedCustomer.email }}</span>
                <span v-if="selectedCustomer.phoneNo">{{ selectedCustomer.phoneNo }}</span>
                <span v-if="selectedCustomer.website" class="truncate">{{ selectedCustomer.website }}</span>
              </div>
            </div>
            <UButton
              icon="i-heroicons-x-mark-20-solid"
              color="neutral"
              variant="ghost"
              size="xs"
              aria-label="Change customer"
              @click="clearCustomer"
            />
          </div>

          <div v-else-if="identityMode === 'search'" class="flex flex-col gap-2">
            <USelectMenu
              v-model="selectedMenuValue"
              v-model:search-term="customerSearch"
              :items="customerMenuItems"
              :loading="searching"
              ignore-filter
              by="value"
              value-key="value"
              label-key="label"
              placeholder="Search by name, email or phone…"
              :filter-fields="['label', 'email', 'phoneNo', 'companyName', 'wuId']"
              class="w-full"
              :ui="{
                content: 'w-[var(--reka-combobox-trigger-width)] max-h-[min(22rem,var(--reka-combobox-content-available-height))]',
                viewport: 'max-h-[18rem] overflow-y-auto divide-y divide-default p-0',
                item: 'p-0',
              }"
            >
              <template #item="{ item }">
                <div class="flex w-full min-w-0 items-start gap-3 px-3 py-2.5">
                  <div class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <UIcon name="i-heroicons-user-solid" class="h-4 w-4" />
                  </div>

                  <div class="min-w-0 flex-1 space-y-1.5">
                    <div class="flex min-w-0 flex-wrap items-center gap-1.5">
                      <span class="truncate text-sm font-semibold text-text-primary">{{ item.name }}</span>
                      <UBadge v-if="!item.active" variant="subtle" size="sm" color="error">Deactivated</UBadge>
                      <UBadge v-if="item.guestUser" variant="subtle" size="sm" color="warning">Guest</UBadge>
                    </div>

                    <p v-if="item.companyName" class="truncate text-xs font-medium text-primary">
                      {{ item.companyName }}
                    </p>

                    <div class="flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-secondary">
                      <span class="inline-flex items-center gap-1 tabular-nums">
                        <UIcon name="i-heroicons-identification-20-solid" class="h-3.5 w-3.5 shrink-0 opacity-70" />
                        {{ item.wuId }}
                      </span>
                      <span
                        v-if="item.email"
                        class="inline-flex min-w-0 max-w-full items-center gap-1"
                      >
                        <UIcon name="i-heroicons-envelope-20-solid" class="h-3.5 w-3.5 shrink-0 opacity-70" />
                        <span class="truncate">{{ item.email }}</span>
                      </span>
                      <span
                        v-if="item.phoneNo"
                        class="inline-flex items-center gap-1"
                      >
                        <UIcon name="i-heroicons-phone-20-solid" class="h-3.5 w-3.5 shrink-0 opacity-70" />
                        {{ item.phoneNo }}
                      </span>
                      <span
                        v-if="item.totalOrders != null"
                        class="inline-flex items-center gap-1"
                      >
                        <UIcon name="i-heroicons-shopping-bag-20-solid" class="h-3.5 w-3.5 shrink-0 opacity-70" />
                        {{ item.totalOrders }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>

              <template #empty>
                <div class="px-3 py-6 text-center text-sm text-text-secondary">
                  <template v-if="customerSearch.trim().length < 3">
                    Type at least 3 characters to search
                  </template>
                  <template v-else-if="searching">
                    Searching…
                  </template>
                  <template v-else-if="searchError">
                    {{ searchError }}
                  </template>
                  <template v-else-if="hasSearched">
                    No customers found
                  </template>
                  <template v-else>
                    Search for a customer
                  </template>
                </div>
              </template>
            </USelectMenu>
          </div>

          <div v-else class="grid grid-cols-1 gap-3">
            <UFormField label="Name" required>
              <UInput
                v-model="customerName"
                placeholder="Customer name"
                size="md"
                class="w-full"
              />
            </UFormField>
            <UFormField label="Email" required>
              <UInput
                v-model="customerEmail"
                type="email"
                placeholder="customer@example.com"
                size="md"
                class="w-full"
              />
            </UFormField>
          </div>
        </div>

        <!-- Report -->
        <div v-else-if="item.value === 'report'">
          <div
            v-if="identitySummary"
            class="mb-4 flex items-center gap-2 rounded-lg border border-default bg-elevated/40 px-3 py-2 text-sm"
          >
            <UIcon name="i-heroicons-user-20-solid" class="h-4 w-4 shrink-0 text-text-secondary" />
            <span class="min-w-0 truncate text-text-primary">{{ identitySummary }}</span>
          </div>

          <WebuserFailedPointCodeReportPanel
            v-if="identityReady && effectiveClientId"
            :key="`${effectiveClientId}-${selectedWuId}`"
            embedded
            :show-header="false"
            active
            :client-id="effectiveClientId"
            :wu-id="selectedWuId"
            :customer-name="resolvedCustomerName"
            :customer-email="resolvedCustomerEmail"
          />

          <p v-else class="text-sm text-text-secondary">
            Complete the steps above to file a report.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { StepperItem } from '@nuxt/ui'
import type { CustomersResponse } from '~~/server/api/misc/search/customer/search.get'

type CustomerResult = CustomersResponse['customers'][number]
type IdentityMode = 'search' | 'manual'
type StepValue = 'client' | 'customer' | 'report'

interface Props {
  clientId?: number
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { selectedClient } = storeToRefs(userStore)

const lockedClientId = computed(() => props.clientId ?? null)
const effectiveClientId = computed(() => lockedClientId.value ?? selectedClient.value?.id ?? null)

const needsClientStep = computed(() => !lockedClientId.value)

const activeStep = ref<StepValue>(needsClientStep.value ? 'client' : 'customer')

const identityMode = ref<IdentityMode>('search')
const customerSearch = ref('')
const searching = ref(false)
const hasSearched = ref(false)
const searchError = ref('')
const searchResults = ref<CustomerResult[]>([])
const selectedCustomer = ref<CustomerResult | null>(null)
const customerName = ref('')
const customerEmail = ref('')

const selectedWuId = computed(() => selectedCustomer.value?.wuId ?? 0)

const resolvedCustomerName = computed(() =>
  selectedCustomer.value?.name || customerName.value.trim()
)
const resolvedCustomerEmail = computed(() =>
  selectedCustomer.value?.email || customerEmail.value.trim()
)

const identityReady = computed(() => {
  if (selectedCustomer.value) return true
  if (identityMode.value !== 'manual') return false
  return resolvedCustomerName.value !== '' && resolvedCustomerEmail.value !== ''
})

interface CustomerMenuItem extends CustomerResult {
  label: string
  value: number
}

const selectedMenuValue = computed<number | undefined>({
  get: () => selectedCustomer.value?.wuId,
  set: (wuId) => {
    if (wuId == null) {
      clearCustomer()
      return
    }
    const found = searchResults.value.find(customer => customer.wuId === wuId)
    if (found) selectCustomer(found)
  },
})

const customerMenuItems = computed<CustomerMenuItem[]>(() =>
  searchResults.value.map(customer => ({
    ...customer,
    label: customer.name,
    value: customer.wuId,
  }))
)

const identitySummary = computed(() => {
  if (!identityReady.value) return ''
  const parts = [
    resolvedCustomerName.value,
    selectedCustomer.value?.companyName,
    resolvedCustomerEmail.value,
    selectedCustomer.value?.phoneNo,
    selectedWuId.value > 0 ? `ID ${selectedWuId.value}` : '',
  ].filter(Boolean)
  return parts.join(' · ')
})


const stepItems = computed<StepperItem[]>(() => {
  const items: StepperItem[] = []

  if (needsClientStep.value) {
    items.push({
      value: 'client',
      title: 'Client',
      description: 'Select client',
      icon: 'i-heroicons-building-office-2',
    })
  }

  items.push({
    value: 'customer',
    title: 'Customer',
    description: 'Who is this about?',
    icon: 'i-heroicons-user',
    disabled: !effectiveClientId.value,
  })

  items.push({
    value: 'report',
    title: 'Report',
    description: 'Log the code',
    icon: 'i-heroicons-flag',
    disabled: !identityReady.value,
  })

  return items
})

function isStepComplete(step: string | number | undefined) {
  if (step === 'client') return !!effectiveClientId.value
  if (step === 'customer') return identityReady.value
  return false
}

function indicatorClass(step: string | number | undefined) {
  if (isStepComplete(step)) {
    return 'border-primary bg-primary text-white'
  }
  if (activeStep.value === step) {
    return 'border-primary text-primary bg-primary/10'
  }
  return 'border-default text-text-secondary bg-default'
}

function focusStep(step: string | number | undefined) {
  if (step !== 'client' && step !== 'customer' && step !== 'report') return
  const item = stepItems.value.find(s => s.value === step)
  if (item?.disabled) return
  activeStep.value = step
}

function selectCustomer(customer: CustomerResult) {
  selectedCustomer.value = customer
  customerName.value = customer.name || ''
  customerEmail.value = customer.email || ''
  searchResults.value = []
  hasSearched.value = false
  customerSearch.value = ''
  searchError.value = ''
  identityMode.value = 'search'
  activeStep.value = 'report'
}

function clearCustomer() {
  selectedCustomer.value = null
  customerName.value = ''
  customerEmail.value = ''
  customerSearch.value = ''
  searchResults.value = []
  hasSearched.value = false
  searchError.value = ''
  activeStep.value = 'customer'
}

function switchToManual() {
  clearCustomer()
  identityMode.value = 'manual'
}

async function searchCustomers() {
  const query = customerSearch.value.trim()
  if (!effectiveClientId.value || query.length < 3) {
    searchResults.value = []
    hasSearched.value = false
    return
  }

  searching.value = true
  searchError.value = ''
  hasSearched.value = true

  try {
    searchResults.value = await $fetch<CustomerResult[]>('/api/misc/search/customer/search', {
      query: { q: query, clientId: effectiveClientId.value },
      headers: useRequestHeaders(['cookie']) as HeadersInit,
    })
  } catch (error: unknown) {
    const err = error as { data?: { message?: string }; message?: string }
    searchError.value = err.data?.message || err.message || 'Failed to search customers'
    searchResults.value = []
  } finally {
    searching.value = false
  }
}

const debouncedSearch = useDebounceFn(searchCustomers, 350)

watch(customerSearch, (value) => {
  if (selectedCustomer.value) return
  if (value.trim().length < 3) {
    searchResults.value = []
    hasSearched.value = false
    searchError.value = ''
    return
  }
  debouncedSearch()
})

watch(effectiveClientId, (id, prev) => {
  if (id === prev) return
  clearCustomer()
  identityMode.value = 'search'
  activeStep.value = id ? 'customer' : (needsClientStep.value ? 'client' : 'customer')
})

watch(identityReady, (ready) => {
  if (ready) activeStep.value = 'report'
  else if (activeStep.value === 'report') activeStep.value = 'customer'
})
</script>
